import React, { useEffect, useState } from "react";
import {
  Box,
  Toolbar,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  TextField,
  Pagination,
  Avatar,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Checkbox,
  FormControlLabel,
  List,
  ListItem,
  ListItemText,
} from "@mui/material";
import { getAllCategories } from "../../api-services/superAdmin";
import { Loading } from "../../components";
import {
  getAllServices,
  handleCategoryDelete,
  handleCategoryUpdate,
  handleCategoryCreate,
  assignServicesToCategory,
  getCategoryServices,
} from "../../api-services/superAdmin";
import { apiRequestStatuses } from "../../common/constant/index";
import { formatDateTime } from "../../utils/common";
import { useDispatch } from "react-redux";
import { notification } from "../../redux/slices/authSlice";
import {
  Edit as EditIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  Circle as CircleIcon,
  Settings as SettingsIcon,
} from "@mui/icons-material";

const Categories = () => {
  const dispatch = useDispatch();

  const [Categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [Category, setCategory] = useState({
    name: "",
    id: "",
    imageUrl: "",
    isGeneric: true,
  });
  const [open, setOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [paginationn, setPagination] = useState({
    currentPage: 0,
    limit: 0,
    totalPages: 0,
    totalRecords: 0,
  });

  // 👇 NEW STATE FOR ASSIGN SERVICES MODAL
  const [assignModalOpen, setAssignModalOpen] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [allServices, setAllServices] = useState([]);
  const [assignedServiceIds, setAssignedServiceIds] = useState([]);
  const [loadingServices, setLoadingServices] = useState(false);

  const fetchCategories = async (name, page) => {
    try {
      if (!name) name = "";
      const response = await getAllCategories(name, page);

      const { categories, pagination } = response.payload.data;
      setCategories(categories);
      setPagination({
        currentPage: pagination.currentPage,
        limit: pagination.limit,
        totalRecords: pagination.totalRecords,
        totalPages: pagination.totalPages,
      });
    } catch (err) {
      console.error("Error fetching Categories:", err);
      setError("Error loading Categories. Please try again later.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCategories();
  }, []);

  const handleClickOpen = (cat = { id: null, name: "" }) => {
    setOpen(true);
    if (cat) {
      setCategory({
        id: cat?._id,
        name: cat?.categoryName,
        imageUrl: cat?.imageUrl,
        isGeneric: cat?.isGeneric,
      });
    }
  };

  const handleChange = (event) => {
    const { name, value, type, checked } = event.target;
    setCategory((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    let res;
    let message;
    if (Category.id) {
      message = "Updated";
      res = await handleCategoryUpdate(Category);
    } else {
      message = "Created";
      res = await handleCategoryCreate(
        Category.name,
        Category.imageUrl,
        Category.isGeneric
      );
    }
    if (res.metadata.status === apiRequestStatuses.SUCCESS) {
      fetchCategories();
      dispatch(
        notification({
          open: true,
          type: "success",
          message: `Category ${message} successfully!`,
        })
      );
    }
    setOpen(false);
  };

  const handlePageChange = async (event, newPage) => {
    setPagination((prevState) => ({
      ...prevState,
      currentPage: newPage,
    }));
    await fetchCategories(searchTerm, newPage);
  };

  const handleSearchChange = async (event) => {
    const name = event.target.value;
    setSearchTerm(name);
    fetchCategories(name);
  };

  const handleStatusUpdate = async (cat) => {
    const updatedCategory = {
      id: cat?._id,
      name: cat?.categoryName,
      isActive: !cat?.isActive,
    };

    handleCategoryUpdate(updatedCategory).then((res) => {
      if (res.metadata.status === apiRequestStatuses.SUCCESS) {
        fetchCategories();
        dispatch(
          notification({
            open: true,
            type: "success",
            message: "Category Updated successfully!",
          })
        );
      }
    });
  };

  const handleDelete = async (id) => {
    const res = await handleCategoryDelete(id);
    if (res.metadata.status === apiRequestStatuses.SUCCESS) {
      fetchCategories();
      dispatch(
        notification({
          open: true,
          type: "success",
          message: "Category Deleted successfully!",
        })
      );
    }
  };

  const handleClose = () => {
    setOpen(false);
  };

  // 👇 NEW HANDLER — OPEN ASSIGN SERVICES MODAL
  const handleOpenAssignModal = async (cat) => {
    setSelectedCategory(cat);
    setAssignModalOpen(true);
    setLoadingServices(true);

    try {
      // Fetch all services
      const allResponse = await getAllServices("", 1);
      setAllServices(allResponse.payload.data.services || []);

      // Fetch currently assigned services
      const assignedResponse = await getCategoryServices(cat._id);
      const assignedIds = (assignedResponse.payload.data.services || []).map(s => s._id);
      setAssignedServiceIds(assignedIds);
    } catch (err) {
      console.error("Error loading services:", err);
      dispatch(
        notification({
          open: true,
          type: "error",
          message: "Failed to load services",
        })
      );
    } finally {
      setLoadingServices(false);
    }
  };

  // 👇 NEW HANDLER — SAVE ASSIGNED SERVICES
  const handleAssignServices = async () => {
    if (!selectedCategory) return;

    try {
      const res = await assignServicesToCategory(selectedCategory._id, assignedServiceIds);
      if (res.metadata.status === apiRequestStatuses.SUCCESS) {
        dispatch(
          notification({
            open: true,
            type: "success",
            message: "Services assigned successfully!",
          })
        );
        setAssignModalOpen(false);
      }
    } catch (err) {
      console.error("Error assigning services:", err);
      dispatch(
        notification({
          open: true,
          type: "error",
          message: "Failed to assign services",
        })
      );
    }
  };

  if (loading) {
    return <Loading />;
  }

  if (error) {
    return <Typography variant="body1">{error}</Typography>;
  }

  return (
    <Box
      component="main"
      sx={{ flexGrow: 1, bgcolor: "background.default", p: 3 }}
    >
      <Toolbar />
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Typography variant="h4" gutterBottom>
          Category List
        </Typography>

        <TextField
          variant="outlined"
          placeholder="Search by category name"
          value={searchTerm}
          onChange={handleSearchChange}
          sx={{
            marginBottom: "20px",
            width: "40%",
            "& .MuiOutlinedInput-root": {
              borderRadius: "20px",
            },
          }}
        />

        <AddIcon
          sx={{
            color: "aliceblue",
            cursor: "pointer",
            background: "linear-gradient(90deg, #001F54, #9B2ECA)",
          }}
          onClick={() => handleClickOpen({ id: null, name: "" })}
        />
      </Box>
      {Categories.length > 0 ? (
        <>
          <TableContainer component={Paper}>
            <Table sx={{ minWidth: 650 }} aria-label="categories table">
              <TableHead>
                <TableRow>
                  <TableCell></TableCell>
                  <TableCell sx={{ fontWeight: "bold" }}>
                    Category Name
                  </TableCell>
                  <TableCell sx={{ fontWeight: "bold" }}>UpdateOn</TableCell>
                  <TableCell sx={{ fontWeight: "bold" }}>Status</TableCell>
                  <TableCell sx={{ fontWeight: "bold" }}>Action</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {Categories.map((cat) => (
                  <TableRow key={cat?._id} hover style={{ cursor: "pointer" }}>
                    <TableCell>
                      {cat?.imageUrl ? (
                        <Avatar alt={cat?.categoryName} src={cat?.imageUrl} />
                      ) : (
                        <Avatar>{cat?.categoryName?.charAt(0)}</Avatar>
                      )}
                    </TableCell>
                    <TableCell>{cat?.categoryName || "-"}</TableCell>
                    <TableCell> {formatDateTime(cat?.updatedOn)}</TableCell>
                    <TableCell>
                      {cat?.isActive ? (
                        <CircleIcon
                          sx={{ color: "#9B2ECA" }}
                          onClick={() => handleStatusUpdate(cat)}
                        />
                      ) : (
                        <CircleIcon
                          sx={{ color: "darkgray" }}
                          onClick={() => handleStatusUpdate(cat)}
                        />
                      )}
                    </TableCell>
                    <TableCell>
                      <EditIcon onClick={() => handleClickOpen(cat)} />
                      <DeleteIcon onClick={() => handleDelete(cat?._id)} />
                      {/* 👇 NEW BUTTON */}
                      <SettingsIcon
                        onClick={() => handleOpenAssignModal(cat)}
                        sx={{ ml: 1, cursor: "pointer", color: "#1976d2" }}
                        titleAccess="Manage Services"
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          <Pagination
            count={paginationn.totalPages}
            page={paginationn.currentPage}
            onChange={handlePageChange}
            sx={{ mt: 2, display: "flex", justifyContent: "center" }}
          />
        </>
      ) : (
        <Typography variant="body1">No Categories found.</Typography>
      )}

      {/* 👇 EXISTING CATEGORY EDIT MODAL */}
      <Dialog
        open={open}
        onClose={handleClose}
        PaperProps={{
          component: "form",
          sx: { width: "600px" },
        }}
      >
        <DialogTitle>Category</DialogTitle>
        <DialogContent
          style={{ display: "flex", flexDirection: "column", gap: "16px" }}
        >
          <div style={{ display: "flex", justifyContent: "space-between" }}>
            <TextField
              autoFocus
              required
              margin="dense"
              id="name"
              name="name"
              label="Category Name"
              value={Category.name}
              onChange={handleChange}
              style={{ width: "48%" }}
              variant="standard"
            />

            <TextField
              autoFocus
              required
              margin="dense"
              id="imageUrl"
              name="imageUrl"
              label="Image Url"
              value={Category.imageUrl}
              onChange={handleChange}
              style={{ width: "48%" }}
              variant="standard"
            />
          </div>

          <FormControlLabel
            control={
              <Checkbox
                checked={Category.isGeneric || false}
                onChange={handleChange}
                name="isGeneric"
              />
            }
            label="Is Generic"
          />
        </DialogContent>
        <DialogActions>
          <Button type="submit" onClick={handleSubmit}>
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* 👇 NEW ASSIGN SERVICES MODAL */}
      <Dialog
        open={assignModalOpen}
        onClose={() => setAssignModalOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Assign Services to "{selectedCategory?.categoryName}"</DialogTitle>
        <DialogContent>
          {loadingServices ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
              <Typography>Loading services...</Typography>
            </Box>
          ) : (
            <List>
              {allServices.map((service) => (
                <ListItem key={service._id} dense>
                  <Checkbox
                    edge="start"
                    checked={assignedServiceIds.includes(service._id)}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setAssignedServiceIds([...assignedServiceIds, service._id]);
                      } else {
                        setAssignedServiceIds(
                          assignedServiceIds.filter((id) => id !== service._id)
                        );
                      }
                    }}
                  />
                  <ListItemText
                    primary={service.serviceName}
                    secondary={service.isGeneric ? "Generic" : ""}
                  />
                </ListItem>
              ))}
            </List>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAssignModalOpen(false)}>Cancel</Button>
          <Button onClick={handleAssignServices} variant="contained" color="primary">
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Categories;